import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  Badge,
  Button,
  Card,
  Input,
  Modal,
  Textarea,
} from "../../components/ui";
import { ConfirmModal } from "../../components/ui/Modal";

const DOCS_URL = "/api/v1/fornitori-api/docs";
const BASE = "/integratori-api";

const formatData = (iso) =>
  iso
    ? new Date(iso).toLocaleString("it-IT", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "mai";

/**
 * Integratori API (solo Admin): sistemi terzi, come Smart Kitchen, che inviano
 * interventi e rapportini per conto di piu' fornitori con un'unica credenziale HMAC.
 * Ogni invio indica il fornitore, che deve essere tra quelli abilitati qui.
 */
function IntegratoriApiPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const [integratori, setIntegratori] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null); // { id?, nome, ipConsentiti, emailReferente, fornitoreIds }
  const [filtro, setFiltro] = useState("");
  const [segretoMostrato, setSegretoMostrato] = useState(null); // { id, segreto }
  const [conferma, setConferma] = useState(null); // { tipo: 'ruota'|'elimina'|'email', credenziale }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, elencoFornitori] = await Promise.all([
        apiRequest(BASE),
        apiRequest("/fornitori").catch(() => ({ value: [] })),
      ]);
      setIntegratori(data.value || []);
      setFornitori(
        (elencoFornitori.value || [])
          // GET /fornitori restituisce i campi gia' mappati (id, nome)
          .map((f) => ({ id: f.id, nome: f.nome }))
          .filter((f) => f.id)
          .sort((a, b) => (a.nome || "").localeCompare(b.nome || "")),
      );
    } catch (error) {
      showToast(error.message || "Errore caricamento integratori", "error");
    } finally {
      setLoading(false);
    }
  }, [apiRequest, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const fornitoriFiltrati = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    return q
      ? fornitori.filter((f) => (f.nome || "").toLowerCase().includes(q))
      : fornitori;
  }, [fornitori, filtro]);

  const apriNuovo = () => {
    setFiltro("");
    setForm({
      nome: "",
      ipConsentiti: "",
      emailReferente: "",
      fornitoreIds: [],
    });
  };

  const apriModifica = (c) => {
    setFiltro("");
    setForm({
      id: c.id,
      nome: c.nome,
      ipConsentiti: c.ipConsentiti,
      emailReferente: c.emailReferente,
      fornitoreIds: c.fornitori.map((f) => f.id.toLowerCase()),
    });
  };

  const toggleFornitore = (id) => {
    const key = id.toLowerCase();
    setForm((f) => ({
      ...f,
      fornitoreIds: f.fornitoreIds.includes(key)
        ? f.fornitoreIds.filter((x) => x !== key)
        : [...f.fornitoreIds, key],
    }));
  };

  const salva = async () => {
    if (!form.nome.trim()) {
      showToast("Il nome e' obbligatorio", "warning");
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await apiRequest(`${BASE}/${form.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            nome: form.nome,
            ipConsentiti: form.ipConsentiti,
            emailReferente: form.emailReferente,
          }),
        });
        await apiRequest(`${BASE}/${form.id}/fornitori`, {
          method: "PUT",
          body: JSON.stringify({ fornitoreIds: form.fornitoreIds }),
        });
        showToast("Integratore aggiornato", "success");
      } else {
        const res = await apiRequest(BASE, {
          method: "POST",
          body: JSON.stringify(form),
        });
        setSegretoMostrato(res);
      }
      setForm(null);
      load();
    } catch (error) {
      showToast(error.message || "Salvataggio non riuscito", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleAttiva = async (c) => {
    try {
      await apiRequest(`${BASE}/${c.id}`, {
        method: "PATCH",
        body: JSON.stringify({ attiva: !c.attiva }),
      });
      load();
    } catch (error) {
      showToast(error.message || "Errore aggiornamento", "error");
    }
  };

  const mostraSegreto = async (c) => {
    try {
      const res = await apiRequest(`${BASE}/${c.id}/segreto`, {
        method: "POST",
      });
      setSegretoMostrato(res);
    } catch (error) {
      showToast(error.message || "Lettura segreto non riuscita", "error");
    }
  };

  const eseguiConferma = async () => {
    const { tipo, credenziale } = conferma;
    setSaving(true);
    try {
      if (tipo === "ruota") {
        const res = await apiRequest(`${BASE}/${credenziale.id}/ruota`, {
          method: "POST",
        });
        setSegretoMostrato(res);
      } else if (tipo === "email") {
        const res = await apiRequest(`${BASE}/${credenziale.id}/invia-email`, {
          method: "POST",
        });
        showToast(res.message || "Credenziali inviate", "success");
      } else {
        await apiRequest(`${BASE}/${credenziale.id}`, { method: "DELETE" });
        showToast("Integratore eliminato", "success");
      }
      setConferma(null);
      load();
    } catch (error) {
      showToast(error.message || "Operazione non riuscita", "error");
    } finally {
      setSaving(false);
    }
  };

  const copia = async (testo) => {
    try {
      await navigator.clipboard.writeText(testo);
      showToast("Copiato negli appunti", "success");
    } catch {
      showToast("Copia non riuscita: seleziona e copia a mano", "warning");
    }
  };

  const testiConferma = {
    ruota: {
      title: "Generare un nuovo segreto?",
      message:
        "Il segreto attuale smette di funzionare subito: l'integratore dovra' aggiornare il proprio sistema. Per cambiarlo senza interruzioni, crea un secondo integratore con gli stessi fornitori e disattiva questo quando ha migrato.",
      confirmText: "Genera",
      confirmVariant: "danger",
    },
    elimina: {
      title: "Eliminare l'integratore?",
      message:
        "L'integratore non potra' piu' inviare per nessun fornitore. Gli interventi gia' ricevuti restano. L'operazione non si puo' annullare.",
      confirmText: "Elimina",
      confirmVariant: "danger",
    },
    email: {
      title: "Inviare le credenziali?",
      message: `Key Id, segreto attuale, IP consentiti, fornitori abilitati e link alla documentazione verranno inviati a ${conferma?.credenziale?.emailReferente}. Il segreto non cambia.`,
      confirmText: "Invia",
      confirmVariant: "primary",
    },
  };
  const tc = conferma ? testiConferma[conferma.tipo] : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Integratori API</h1>
          <p className="text-sm text-gray-500 max-w-3xl">
            Sistemi terzi che inviano interventi e rapportini per conto di piu'
            fornitori con un'unica credenziale (es. un software di gestione
            usato da diversi fornitori). In ogni invio indicano il fornitore,
            che deve essere tra quelli abilitati qui. Gli interventi arrivano da
            validare. Gli utenti dei fornitori non vedono queste credenziali.{" "}
            <a
              href={DOCS_URL}
              target="_blank"
              rel="noreferrer"
              className="text-primary-600 underline"
            >
              Documentazione API
            </a>
          </p>
        </div>
        <Button onClick={apriNuovo}>Nuovo integratore</Button>
      </div>

      <Card>
        {loading ? (
          <p className="text-sm text-gray-500">Caricamento...</p>
        ) : integratori.length === 0 ? (
          <p className="text-sm text-gray-500">Nessun integratore.</p>
        ) : (
          <div className="space-y-3">
            {integratori.map((c) => (
              <div
                key={c.id}
                className="border border-gray-200 rounded-lg p-4 flex flex-wrap gap-4 justify-between"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{c.nome}</span>
                    <Badge variant={c.attiva ? "success" : "default"}>
                      {c.attiva ? "Attiva" : "Disattivata"}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 break-all">
                    Key Id: <code>{c.id}</code>{" "}
                    <button
                      type="button"
                      className="text-primary-600 underline"
                      onClick={() => copia(c.id)}
                    >
                      copia
                    </button>
                  </p>
                  <p className="text-xs text-gray-500">
                    Fornitori abilitati:{" "}
                    {c.fornitori.length > 0
                      ? c.fornitori.map((f) => f.nome).join(", ")
                      : "nessuno"}
                  </p>
                  <p className="text-xs text-gray-500">
                    Email referente: {c.emailReferente || "non impostata"}
                  </p>
                  <p className="text-xs text-gray-500">
                    IP consentiti:{" "}
                    {c.ipConsentiti ? (
                      <code>{c.ipConsentiti.split(/\s+/).join(", ")}</code>
                    ) : (
                      "nessuna restrizione"
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    Ultimo utilizzo: {formatData(c.ultimoUtilizzo)}
                    {c.ultimoIp ? ` da ${c.ultimoIp}` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap items-start gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => apriModifica(c)}
                  >
                    Modifica
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleAttiva(c)}
                  >
                    {c.attiva ? "Disattiva" : "Attiva"}
                  </Button>
                  {c.attiva && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => mostraSegreto(c)}
                      >
                        Mostra segreto
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!c.emailReferente}
                        title={
                          c.emailReferente
                            ? undefined
                            : "Imposta prima l'email referente"
                        }
                        onClick={() =>
                          setConferma({ tipo: "email", credenziale: c })
                        }
                      >
                        Invia via email
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setConferma({ tipo: "ruota", credenziale: c })
                    }
                  >
                    Nuovo segreto
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() =>
                      setConferma({ tipo: "elimina", credenziale: c })
                    }
                  >
                    Elimina
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Creazione / modifica */}
      <Modal
        isOpen={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? "Modifica integratore" : "Nuovo integratore"}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setForm(null)}>
              Annulla
            </Button>
            <Button onClick={salva} disabled={saving}>
              {saving ? "Salvataggio..." : form?.id ? "Salva" : "Crea"}
            </Button>
          </div>
        }
      >
        {form && (
          <div className="space-y-4">
            <Input
              label="Nome"
              placeholder="Smart Kitchen"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
            />
            <Input
              label="Email referente"
              type="email"
              placeholder="it@integratore.it"
              value={form.emailReferente}
              onChange={(e) =>
                setForm({ ...form, emailReferente: e.target.value })
              }
            />
            <p className="text-xs text-gray-500 -mt-2">
              L'unico indirizzo a cui "Invia via email" manda le credenziali.
            </p>
            <Textarea
              label="IP consentiti (facoltativo)"
              rows={2}
              placeholder={"79.8.164.121\n10.0.0.0/24"}
              value={form.ipConsentiti}
              onChange={(e) =>
                setForm({ ...form, ipConsentiti: e.target.value })
              }
            />
            <p className="text-xs text-gray-500 -mt-2">
              Uno per riga: indirizzi IPv4/IPv6 o reti CIDR. Vuoto = nessuna
              restrizione.
            </p>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Fornitori abilitati ({form.fornitoreIds.length})
              </p>
              <Input
                placeholder="Cerca fornitore..."
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
              />
              <div className="mt-2 max-h-56 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                {fornitoriFiltrati.map((f) => (
                  <label
                    key={f.id}
                    className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={form.fornitoreIds.includes(f.id.toLowerCase())}
                      onChange={() => toggleFornitore(f.id)}
                    />
                    <span>{f.nome}</span>
                  </label>
                ))}
                {fornitoriFiltrati.length === 0 && (
                  <p className="px-3 py-2 text-sm text-gray-500">
                    Nessun fornitore trovato
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Segreto appena creato, rigenerato o letto */}
      <Modal
        isOpen={!!segretoMostrato}
        onClose={() => setSegretoMostrato(null)}
        title="Credenziale integratore"
        footer={
          <div className="flex justify-end">
            <Button onClick={() => setSegretoMostrato(null)}>Chiudi</Button>
          </div>
        }
      >
        {segretoMostrato && (
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Puoi inviarlo all'email referente con "Invia via email" o
              rileggerlo da qui con "Mostra segreto".
            </div>
            {[
              ["Key Id", segretoMostrato.id],
              ["Segreto", segretoMostrato.segreto],
            ].map(([etichetta, valore]) => (
              <div key={etichetta}>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {etichetta}
                </p>
                <div className="flex gap-2">
                  <code className="flex-1 p-2 bg-gray-100 rounded text-xs break-all">
                    {valore}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copia(valore)}
                  >
                    Copia
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!conferma}
        onClose={() => setConferma(null)}
        onConfirm={eseguiConferma}
        loading={saving}
        title={tc?.title}
        message={tc?.message}
        confirmText={tc?.confirmText}
        confirmVariant={tc?.confirmVariant}
      />
    </div>
  );
}

export default IntegratoriApiPage;
